import { brands, categories, groups, keywords } from "@sobidogam/catalog";
import type { CatalogCategory } from "@sobidogam/domain";
import { UNLOCK_THRESHOLDS } from "@sobidogam/domain";
import type { Channel } from "@sobidogam/platform-ports";
import type { MouseEvent, ReactNode } from "react";
import { Header, navigate, SiteFooter } from "../chrome";
import { type Guide, guides, PRICING } from "./guides";

/**
 * Public pages rendered both in the browser and at build time
 * (apps/web/src/prerender.tsx → scripts/build-static-pages.mjs). Keep this
 * file free of CSS imports and module-level window access.
 */

function go(href: string) {
	return (event: MouseEvent<HTMLAnchorElement>) => {
		if (href.startsWith("/downloads/")) return;
		event.preventDefault();
		navigate(href);
	};
}

function Link({
	href,
	className,
	children,
	onClick,
}: {
	href: string;
	className?: string;
	children: ReactNode;
	onClick?: () => void;
}) {
	const download = href.startsWith("/downloads/");
	return (
		<a
			className={className}
			href={href}
			download={download ? "" : undefined}
			onClick={(event) => {
				onClick?.();
				go(href)(event);
			}}
		>
			{children}
		</a>
	);
}

export function GuidePage({
	guide,
	channel,
}: {
	guide: Guide;
	channel: Channel;
}) {
	return (
		<>
			<Header channel={channel} />
			<main className="section-wrap guide-page">
				<p className="eyebrow">사용 안내</p>
				<h1>{guide.h1}</h1>
				<p className="page-lede">{guide.lede}</p>
				{guide.sections.map((section) => (
					<section
						className="guide-section"
						key={section.heading}
						id={section.id}
					>
						<h2>{section.heading}</h2>
						{section.paragraphs?.map((text) => (
							<p key={text}>{text}</p>
						))}
						{section.steps ? (
							<ol className="guide-steps">
								{section.steps.map((step) => (
									<li key={step}>{step}</li>
								))}
							</ol>
						) : null}
						{section.list ? (
							<ul className="guide-list">
								{section.list.map((item) => (
									<li key={item}>{item}</li>
								))}
							</ul>
						) : null}
						{section.examples ? (
							<ul className="guide-examples">
								{section.examples.map((example) => (
									<li key={example}>
										<code>{example}</code>
									</li>
								))}
							</ul>
						) : null}
						{section.table ? (
							<div className="guide-table-wrap">
								<table className="guide-table">
									<thead>
										<tr>
											{section.table.head.map((cell) => (
												<th key={cell} scope="col">
													{cell}
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{section.table.rows.map((row) => (
											<tr key={row.join("|")}>
												{row.map((cell, index) => (
													<td key={`${index}-${cell}`}>{cell}</td>
												))}
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : null}
					</section>
				))}
				<div className="guide-cta">
					<Link className="button full-width" href={guide.cta.href}>
						{guide.cta.label}
					</Link>
					{guide.secondary ? (
						<Link className="quiet-button" href={guide.secondary.href}>
							{guide.secondary.label}
						</Link>
					) : null}
				</div>
				<nav className="guide-more" aria-label="다른 안내">
					<h2>다른 안내</h2>
					<ul>
						{guides
							.filter((other) => other.slug !== guide.slug)
							.map((other) => (
								<li key={other.slug}>
									<Link href={`/guide/${other.slug}`}>{other.h1}</Link>
								</li>
							))}
						<li>
							<Link href="/pricing">무료와 유료 범위</Link>
						</li>
					</ul>
				</nav>
			</main>
			<SiteFooter />
		</>
	);
}

export function PricingPage({
	channel,
	onStart,
}: {
	channel: Channel;
	onStart?: () => void;
}) {
	return (
		<>
			<Header channel={channel} />
			<main className="section-wrap pricing-page">
				<p className="eyebrow">가격</p>
				<h1>기록은 평생 무료예요</h1>
				<p className="page-lede">
					매일 쓰는 기록과 정리는 무료로 두고, 오래 간직할 회고와 소장용 결산만
					한 번 구매해요. 광고는 넣지 않아요.
				</p>
				<section className="pricing-free">
					<h2>무료로 계속 쓰는 것</h2>
					<ul className="guide-list">
						{PRICING.free.map((item) => (
							<li key={item}>{item}</li>
						))}
					</ul>
				</section>
				<section className="pricing-paid">
					<h2>한 번 구매하는 것</h2>
					{PRICING.paid.map((offer) => (
						<article className="pricing-offer" key={offer.name}>
							<header>
								<strong>{offer.name}</strong>
								<span className="pricing-amount">{offer.price}</span>
							</header>
							<small>{offer.kind}</small>
							<p>{offer.body}</p>
						</article>
					))}
				</section>
				<section className="pricing-rules">
					<h2>결제·환불·복원</h2>
					<ul className="guide-list">
						{PRICING.rules.map((rule) => (
							<li key={rule}>{rule}</li>
						))}
					</ul>
				</section>
				<div className="guide-cta">
					<Link
						className="button full-width"
						href="/app/capture"
						{...(onStart ? { onClick: onStart } : {})}
					>
						무료로 기록 시작하기
					</Link>
				</div>
			</main>
			<SiteFooter />
		</>
	);
}

/** Example store whose name really classifies into this cell, if any. */
function storeExample(category: CatalogCategory): string | null {
	const brand = brands
		.filter((item) => item.categoryId === category.categoryId)
		.sort((a, b) => a.sortOrder - b.sortOrder)[0];
	if (brand) return brand.nameKo;
	const keyword = keywords.find(
		(item) => item.categoryId === category.categoryId,
	);
	return keyword ? `동네${keyword.pattern}` : null;
}

export function categoryNotificationExample(
	category: CatalogCategory,
): string | null {
	const store = storeExample(category);
	return store ? `신한카드 10/16 12:30 ${store} 8,500원 승인` : null;
}

export function CategoryDetail({
	category,
	channel,
	onCta,
}: {
	category: CatalogCategory;
	channel: Channel;
	onCta?: () => void;
}) {
	const group = groups.find((item) => item.groupId === category.groupId);
	const cellBrands = brands
		.filter((brand) => brand.categoryId === category.categoryId)
		.sort((a, b) => a.sortOrder - b.sortOrder)
		.slice(0, 8);
	const cellKeywords = keywords.filter(
		(keyword) => keyword.categoryId === category.categoryId,
	);
	const example = category.sensitive
		? null
		: categoryNotificationExample(category);
	const similar = categories
		.filter(
			(item) =>
				item.groupId === category.groupId &&
				item.categoryId !== category.categoryId,
		)
		.sort(
			(a, b) =>
				Math.abs(a.sortOrder - category.sortOrder) -
				Math.abs(b.sortOrder - category.sortOrder),
		)
		.slice(0, 3);
	return (
		<>
			<Header channel={channel} />
			<main className="section-wrap catalog-detail">
				<Link className="text-link" href="/dogam">
					← 전체 도감
				</Link>
				<img
					className="detail-illustration"
					src={`/illustrations/${category.categoryId}.svg`}
					alt=""
					width={160}
					height={160}
				/>
				<p className="eyebrow">
					{group?.nameKo} · {String(category.sortOrder).padStart(3, "0")}
				</p>
				<h1>{category.nameKo}</h1>
				<p className="detail-copy">{category.descKo}</p>
				{category.sensitive ? (
					<div className="notice notice-sage">
						이 업종은 품목과 메모를 저장하지 않아요. 매장 이름 대신 업종과
						총액만 남기고, 공유 카드도 만들지 않아요.
					</div>
				) : null}
				{cellBrands.length || cellKeywords.length ? (
					<section className="detail-block">
						<h2>이 칸에 들어가는 곳</h2>
						<ul className="detail-chips">
							{cellBrands.map((brand) => (
								<li key={brand.brandId}>{brand.nameKo}</li>
							))}
							{cellKeywords.map((keyword) => (
								<li key={keyword.pattern}>이름에 '{keyword.pattern}'</li>
							))}
						</ul>
						<p className="field-hint">
							여기 없는 곳도 분류를 한 번 고르면 다음부터 기억해요.
						</p>
					</section>
				) : null}
				{example ? (
					<section className="detail-block">
						<h2>이런 알림이면 이 칸이 열려요</h2>
						<p className="detail-example">
							<code>{example}</code>
						</p>
					</section>
				) : null}
				<section className="detail-block">
					<h2>칸이 또렷해지는 단계</h2>
					<ol className="detail-stages">
						<li>
							<b>{UNLOCK_THRESHOLDS[0]}번</b> 기록하면 칸이 열려요.
						</li>
						<li>
							<b>{UNLOCK_THRESHOLDS[1]}번</b>이면 모습이 또렷해져요.
						</li>
						<li>
							<b>{UNLOCK_THRESHOLDS[2]}번</b>이 넘으면 단골 표본이 돼요.
						</li>
					</ol>
				</section>
				{category.sensitive ? null : (
					<Link
						className="button full-width"
						href={`/app/capture?category=${category.slug}`}
						{...(onCta ? { onClick: onCta } : {})}
					>
						이 칸에 첫 기록 남기기
					</Link>
				)}
				{similar.length ? (
					<nav className="detail-block" aria-label="비슷한 칸">
						<h2>비슷한 칸</h2>
						<ul className="detail-similar">
							{similar.map((item) => (
								<li key={item.categoryId}>
									<Link href={`/dogam/${item.slug}`}>
										<img
											src={`/illustrations/${item.categoryId}.svg`}
											alt=""
											width={44}
											height={44}
										/>
										<span>{item.nameKo}</span>
									</Link>
								</li>
							))}
						</ul>
					</nav>
				) : null}
			</main>
			<SiteFooter />
		</>
	);
}

export function CatalogIndex({ channel }: { channel: Channel }) {
	return (
		<>
			<Header channel={channel} />
			<main className="section-wrap catalog-page">
				<p className="eyebrow">소비 도감</p>
				<h1>소비 도감 128칸</h1>
				<p className="page-lede">
					결제 한 건이 칸 하나를 열어요. 카페부터 반려동물까지, 칸을 눌러 어떤
					결제가 들어가는지 보세요.
				</p>
				<div className="catalog-list">
					{groups.map((group) => (
						<section key={group.groupId}>
							<h2>
								{group.nameKo}
								<small>
									{
										categories.filter(
											(category) => category.groupId === group.groupId,
										).length
									}
									칸
								</small>
							</h2>
							<div className="catalog-links">
								{categories
									.filter((category) => category.groupId === group.groupId)
									.map((category) => (
										<Link
											key={category.categoryId}
											href={`/dogam/${category.slug}`}
										>
											<img
												src={`/illustrations/${category.categoryId}.svg`}
												alt=""
												width={32}
												height={32}
											/>
											<span>{category.nameKo}</span>
											<span className="link-arrow">→</span>
										</Link>
									))}
							</div>
						</section>
					))}
				</div>
				<nav className="guide-more" aria-label="사용 안내">
					<h2>사용 안내</h2>
					<ul>
						{guides.map((guide) => (
							<li key={guide.slug}>
								<Link href={`/guide/${guide.slug}`}>{guide.h1}</Link>
							</li>
						))}
					</ul>
				</nav>
			</main>
			<SiteFooter />
		</>
	);
}
